import clsx from 'clsx';
import { isPast, isToday, isBefore } from 'date-fns';
import Tooltip from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap.css';

import styles from './styles.module.scss';

import { BaseComponentProps } from '@utils/types/baseComponents';
import {
  CalendarMonth as CalendarMonthType,
  DatesRange,
} from '@utils/types/calendar';
import {
  getDayTooltipText,
  isDayFirstInInterval,
  isDayInInvalidRange,
  isDayInTheRange,
  isDayLastInInterval,
  isDayUnavailableDueToMinNightRules,
  isDayUnavailableDueToMaxNightRules,
  isDayAValidStartDate,
  isDayAValidEndDate,
  isDayPastLastKnownDay,
  doesDayHaveRangeBetweenItselfAndSelectedFrom,
} from './helpers';

type Props = {
  month: CalendarMonthType;
  selectedDates: DatesRange;
  disabledDates: string[];
  onDateSelect: (date: Date) => void;
  minNightsStay?: number;
  maxNightsStay?: number;
  lastKnownDay?: string | null;
  selectNewEndDateMode?: boolean;
} & BaseComponentProps;

const ListingMonth = (props: Props) => {
  const {
    month: { days, month, year },
    selectedDates,
    disabledDates,
    onDateSelect,
    minNightsStay,
    maxNightsStay,
    lastKnownDay,
    selectNewEndDateMode,
  } = props;

  return (
    <div className={styles.month}>
      <div className={styles.days}>
        {days.map((day, idx) => {
          if (typeof day === 'number') {
            const date = new Date(year, month, day);

            const isSelected = isDayInTheRange(date, selectedDates);

            const isPastLastKnownDay = isDayPastLastKnownDay(
              date,
              lastKnownDay
            );

            const isFirstDayInInterval = isDayFirstInInterval(
              date,
              selectedDates
            );

            const isLastDayInInterval = isDayLastInInterval(
              date,
              selectedDates
            );

            /**
             * When extending reservation, we want to apply this rule as if
             * there is no selected end date, so we can select a new one.
             */
            const isBeforeSelectedFrom =
              (!selectedDates.to || !!selectNewEndDateMode) &&
              isBefore(date, selectedDates.from || 0);

            const isUnavailableDueToMinNightRules =
              isDayUnavailableDueToMinNightRules(
                date,
                selectedDates,
                minNightsStay
              );

            const isUnavailableDueToMaxNightRules =
              isDayUnavailableDueToMaxNightRules(
                date,
                /**
                 * When extending reservation, we want to apply this rule as if
                 * there is no selected end date. But the rule should be applied
                 * to the original check-in date.
                 */
                selectNewEndDateMode
                  ? {
                      from: selectedDates.from,
                      to: null,
                    }
                  : selectedDates,
                maxNightsStay
              );

            const isUnavailableDueToInvalidRange = isDayInInvalidRange(
              date,
              /**
               * When extending reservation, we want to apply this rule as if
               * there is no selected end date.
               * This will allow us to select a new end date even if it's
               * a first day of a following reservation.
               */
              selectNewEndDateMode
                ? {
                    from: selectedDates.from,
                    to: null,
                  }
                : selectedDates,
              disabledDates
            );

            const doesHaveRangeBetweenItselfAndSelectedFrom =
              doesDayHaveRangeBetweenItselfAndSelectedFrom(
                date,
                /**
                 * When extending reservation, we want to apply this rule as if
                 * there is no selected end date.
                 */
                selectNewEndDateMode
                  ? {
                      from: selectedDates.to,
                      to: null,
                    }
                  : selectedDates,
                disabledDates
              );

            const isUnavailable =
              isUnavailableDueToMinNightRules ||
              isUnavailableDueToMaxNightRules ||
              isBeforeSelectedFrom ||
              isUnavailableDueToInvalidRange ||
              doesHaveRangeBetweenItselfAndSelectedFrom;

            const isDisabled =
              isPast(date) || isPastLastKnownDay || isUnavailable;

            const isValidStartDate =
              !isDisabled &&
              isDayAValidStartDate(
                date,
                /**
                 * When extending reservation, we want to apply this rule as if
                 * there is no selected end date.
                 **/
                selectNewEndDateMode
                  ? {
                      from: selectedDates.to,
                      to: null,
                    }
                  : selectedDates,
                disabledDates,
                minNightsStay
              );

            const isValidEndDate =
              !isDisabled &&
              isDayAValidEndDate(
                date,
                /**
                 * When extending reservation, we want to apply this rule as if
                 * there is no selected end date.
                 **/
                selectNewEndDateMode
                  ? {
                      from: selectedDates.to,
                      to: null,
                    }
                  : selectedDates,
                disabledDates,
                minNightsStay,
                selectNewEndDateMode
              );

            const isAvailableButNotSelectable =
              !isDisabled &&
              !isSelected &&
              !isValidStartDate &&
              !isValidEndDate;

            const tooltipText = getDayTooltipText({
              date,
              isUnavailableDueToMinNightRules,
              isUnavailableDueToMaxNightRules,
              isFirstDayInInterval,
              isLastDayInInterval,
              isBeforeSelectedFrom,
              isDisabled,
              isAvailableButNotSelectable,
              isSelected,
              isValidStartDate,
              isValidEndDate,
              minNightsStay,
              maxNightsStay,
              selectNewEndDateMode,
            });

            return (
              <Tooltip
                key={`${day}-${idx}`}
                placement="top"
                overlay={<span>{tooltipText}</span>}
                overlayClassName="calendar-listing-tooltip"
                mouseLeaveDelay={0}
              >
                <div
                  onClick={() => {
                    if (
                      isDisabled ||
                      isAvailableButNotSelectable ||
                      isSelected ||
                      isFirstDayInInterval ||
                      isLastDayInInterval
                    ) {
                      return;
                    }
                    onDateSelect(date);
                  }}
                  className={clsx([
                    styles.dayInMonth,
                    day && styles.dayInMonth__withOutline,
                    isToday(date) && styles.dayInMonth__today,
                    (isFirstDayInInterval || isLastDayInInterval) &&
                      styles.dayInMonth__firstOrLastInInterval,
                    isValidStartDate && styles.dayInMonth__validStartDate,
                    isDisabled && styles.dayInMonth__disabled,
                    isSelected && styles.dayInMonth__selected,
                    isAvailableButNotSelectable && styles.dayInMonth__available,
                    isUnavailableDueToMinNightRules &&
                      styles.dayInMonth__unavailableDueToMinNigthsRule,
                    selectNewEndDateMode &&
                      (isFirstDayInInterval ||
                        isLastDayInInterval ||
                        isSelected) &&
                      styles.dayInMonth__oldSelected,
                  ])}
                >
                  {date.getDate()}
                </div>
              </Tooltip>
            );
          }
          return <div key={`${day}-${idx}`} />;
        })}
      </div>
    </div>
  );
};

export { ListingMonth };
